import produce from 'immer';
import { without } from 'ramda';
import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import {
  TableGridApiAction,
  TableGridApiActions,
  TableGridApiProps,
  TableGridApiProviderProps,
} from './TableGridApiContext.types';

const TableGridApiContext = createContext<TableGridApiProps | undefined>(undefined);
const TableGridApiDispatchContext = createContext<Dispatch<TableGridApiActions> | undefined>(
  undefined,
);
const initialState: TableGridApiProps = {
  tableGridApi: undefined,
  rowSelectionIdGetter: undefined,
  selectedRows: [],
  rowsAndIdsOrder: {},
};

const TableGridApiReducer = (state: TableGridApiProps, action: TableGridApiActions) => {
  switch (action.type) {
    case TableGridApiAction.SetTableApi: {
      return produce(state, (draft) => {
        draft.tableGridApi = action.payload.tableGridApi;
        draft.rowSelectionIdGetter = action.payload.rowSelectionIdGetter;
      });
    }
    case TableGridApiAction.SetInitialSelectedRows: {
      return produce(state, (draft) => {
        draft.selectedRows = action.payload;
      });
    }
    case TableGridApiAction.ResetSelectedRows: {
      return produce(state, (draft) => {
        draft.selectedRows = [];
      });
    }
    case TableGridApiAction.AddRowsAndIdsOrder: {
      return produce(state, (draft) => {
        draft.rowsAndIdsOrder = action.payload;
      });
    }
    case TableGridApiAction.AddSelectedRows: {
      return produce(state, (draft) => {
        // set state
        const ids = state.selectedRows.concat(action.payload);
        const tablesRowsOrder: any = state.rowsAndIdsOrder;

        // ordering ids by tablesRowsOrder
        const sortFunction = (a: string, b: string) => {
          const indexA = tablesRowsOrder[a];
          const indexB = tablesRowsOrder[b];
          const radix = 10;

          if (parseInt(indexA, radix) < parseInt(indexB, radix)) {
            return -1;
          } else if (parseInt(indexA, radix) > parseInt(indexB, radix)) {
            return 1;
          } else {
            return 0;
          }
        };
        ids.sort(sortFunction);

        draft.selectedRows = ids;
      });
    }
    case TableGridApiAction.RemoveSelectedRows: {
      return produce(state, (draft) => {
        draft.selectedRows = without(action.payload, state.selectedRows);
      });
    }

    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

export const TableGridApiContextProvider = ({ children }: TableGridApiProviderProps) => {
  const [state, dispatch] = useReducer(TableGridApiReducer, initialState);

  return (
    <TableGridApiContext.Provider value={state}>
      <TableGridApiDispatchContext.Provider value={dispatch}>
        {children}
      </TableGridApiDispatchContext.Provider>
    </TableGridApiContext.Provider>
  );
};

export const useTableGridApiState = () => {
  const context = useContext(TableGridApiContext);

  if (context === undefined) {
    throw new Error('TableGridApiState must be used within a TableGridApiProvider');
  }

  return context;
};

export const useTableGridApiDispatch = () => {
  const context = useContext(TableGridApiDispatchContext);

  if (context === undefined) {
    throw new Error('TableGridApiDispatch must be used within a TableGridApiProvider');
  }

  return context;
};
